import React, { useState } from "react";
import { Alert, Box, Button, Grid, InputAdornment, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { looksLikeCompanyName } from "../utils/clientIdentityHelpers";

type Breakpoint = "xs" | "sm" | "md";

interface Props {
  esEmpresa: boolean;
  nombre: string;
  apellido: string;
  /** Emite el evento del input (name "nombre" o "apellido"), como el resto del formulario. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  errores?: { nombre?: string; apellido?: string };
  bloqueado?: { nombre?: boolean; apellido?: boolean };
  /** "placeholder": el texto va dentro del campo (nueva reserva); "etiqueta": arriba (modal y edición). */
  estilo?: "etiqueta" | "placeholder";
  icono?: React.ReactNode;
  /** Ancho de cada campo de una persona; la razón social de una empresa ocupa el doble. */
  columnas?: Partial<Record<Breakpoint, number>>;
  /** Si se pasa, una persona con nombre y apellido iguales ofrece pasar a empresa. */
  onPasarAEmpresa?: () => void;
  textFieldSx?: SxProps<Theme>;
}

const duplicar = (columnas: Partial<Record<Breakpoint, number>>) =>
  Object.fromEntries(
    Object.entries(columnas).map(([punto, ancho]) => [punto, Math.min(12, (ancho ?? 6) * 2)])
  ) as Partial<Record<Breakpoint, number>>;

/**
 * Nombre del titular. Persona: nombre y apellido. Empresa: un solo campo de
 * razón social y la sucursal escondida detrás de "Agregar sucursal": con dos
 * casilleros a la vista se completan los dos por reflejo, y así nacían las
 * empresas con el nombre pegado dos veces.
 */
const NombreClienteFields: React.FC<Props> = ({
  esEmpresa,
  nombre,
  apellido,
  onChange,
  errores = {},
  bloqueado = {},
  estilo = "etiqueta",
  icono,
  columnas = { xs: 12, sm: 6 },
  onPasarAEmpresa,
  textFieldSx,
}) => {
  const [sucursalAbierta, setSucursalAbierta] = useState(false);
  const mostrarSucursal = sucursalAbierta || Boolean(apellido.trim());

  const textos = (texto: string, obligatorio: boolean) => {
    const conMarca = obligatorio ? `${texto} *` : texto;
    return estilo === "placeholder"
      ? { placeholder: conMarca, inputProps: { "aria-label": texto } }
      : { label: texto, required: obligatorio };
  };
  const adorno = icono ? { startAdornment: <InputAdornment position="start">{icono}</InputAdornment> } : undefined;

  if (!esEmpresa) {
    return (
      <>
        <Grid item {...columnas}>
          <TextField
            fullWidth
            size="small"
            name="nombre"
            value={nombre}
            onChange={onChange}
            disabled={bloqueado.nombre}
            error={!!errores.nombre}
            helperText={errores.nombre}
            InputProps={adorno}
            sx={textFieldSx}
            {...textos("Nombre", true)}
          />
        </Grid>
        <Grid item {...columnas}>
          <TextField
            fullWidth
            size="small"
            name="apellido"
            value={apellido}
            onChange={onChange}
            disabled={bloqueado.apellido}
            error={!!errores.apellido}
            helperText={errores.apellido}
            InputProps={adorno}
            sx={textFieldSx}
            {...textos("Apellido", true)}
          />
        </Grid>
        {onPasarAEmpresa && looksLikeCompanyName(nombre, apellido) ? (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              sx={{ py: 0 }}
              action={
                <Button color="inherit" size="small" onClick={onPasarAEmpresa}>
                  Cambiar a empresa
                </Button>
              }
            >
              Nombre y apellido iguales: ¿es una empresa?
            </Alert>
          </Grid>
        ) : null}
      </>
    );
  }

  return (
    <Grid item {...duplicar(columnas)}>
      <TextField
        fullWidth
        size="small"
        name="nombre"
        value={nombre}
        onChange={onChange}
        disabled={bloqueado.nombre}
        error={!!errores.nombre}
        helperText={errores.nombre}
        InputProps={adorno}
        sx={textFieldSx}
        {...textos("Razón social", true)}
        {...(estilo === "placeholder" ? {} : { placeholder: "Ej. Hoch Ascensores S.R.L." })}
      />
      {mostrarSucursal ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            name="apellido"
            value={apellido}
            onChange={onChange}
            onBlur={() => {
              if (!apellido.trim()) setSucursalAbierta(false);
            }}
            autoFocus={sucursalAbierta && !apellido}
            disabled={bloqueado.apellido}
            error={!!errores.apellido}
            helperText={errores.apellido}
            InputProps={adorno}
            sx={textFieldSx}
            {...textos("Sucursal", false)}
            {...(estilo === "placeholder" ? {} : { placeholder: "Ej. Caleta Olivia" })}
          />
        </Box>
      ) : !bloqueado.apellido ? (
        <Button
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setSucursalAbierta(true)}
          sx={{ mt: 0.5, px: 0.5, textTransform: "none" }}
        >
          Agregar sucursal
        </Button>
      ) : null}
    </Grid>
  );
};

export default NombreClienteFields;
